import { usePrivy } from '@privy-io/react-auth';
import {
  Check,
  CheckCircle,
  ChevronRight,
  Loader2,
  Mail,
  Wallet,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import RewardsCard from '@/components/RewardsCard';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import IsConnectedWrapper from '../components/IsConnectedWrapper';
import {
  useShinkaiIdentityQuery,
  useUpdateSelectedIdentity,
} from '../hooks/shinkai-identity';
import { useDiscordVerifiedStatus } from '../hooks/useDiscordVerification';
import { useEvmWallet } from '../hooks/useEvmWallet';
import { useGetIdentities } from '../hooks/useGetIdentities';
import { useGetIdentityData } from '../hooks/useGetIdentityData';

// Social icons
function DiscordIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-label="Discord"
    >
      <title>Discord</title>
      <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
    </svg>
  );
}

function TwitterIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-label="X (Twitter)"
    >
      <title>X (Twitter)</title>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-label="Google"
    >
      <title>Google</title>
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

function formatStakedKAI(stakedTokens: bigint) {
  const stakedValue = Number(stakedTokens) / 1e18;
  return stakedValue >= 1000
    ? `${(stakedValue / 1000).toFixed(1)}k`
    : stakedValue.toFixed(0);
}

function SectionHeader({
  title,
  subtitle,
  right,
}: {
  title: string;
  subtitle: string;
  right?: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div>
        <h2 className="text-xl font-semibold text-text-default">{title}</h2>
        <p className="text-sm text-text-tertiary mt-1">{subtitle}</p>
      </div>
      {right}
    </div>
  );
}

function IdentityQuickAccessCard({
  identity,
  stakedTokens,
  isActive,
}: {
  identity: string;
  stakedTokens: bigint;
  isActive: boolean;
}) {
  return (
    <Link
      to={`/identity/${encodeURIComponent(identity)}`}
      className="group flex w-full items-center justify-between gap-3 rounded-xl border border-divider bg-bg-tertiary/20 px-3 py-3 text-left transition-all hover:border-brand/30 hover:bg-bg-tertiary/40 hover:shadow-[0_0_0_1px_rgba(var(--brand-rgb),0.15)]"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-8 h-8 rounded-full border border-brand/30 bg-brand/15 text-brand flex items-center justify-center text-xs font-bold shrink-0">
          {identity.charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-text-default">
            {identity}
          </p>
          <p className="text-[10px] text-text-tertiary">
            {formatStakedKAI(stakedTokens)} KAI staked
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {isActive && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-success/20 text-success">
            Rewards
          </span>
        )}
        <ChevronRight className="w-4 h-4 text-text-tertiary group-hover:text-text-default transition-colors" />
      </div>
    </Link>
  );
}

// Identity selection card
function IdentitySelectCard({
  identity,
  stakedTokens,
  isSelected,
  isActive,
  onSelect,
  disabled,
}: {
  identity: string;
  stakedTokens: bigint;
  isSelected: boolean;
  isActive: boolean;
  onSelect: () => void;
  disabled?: boolean;
}) {
  return (
    <label
      className={cn(
        'block cursor-pointer',
        disabled && 'opacity-60 cursor-not-allowed',
      )}
    >
      <input
        type="radio"
        name="rewards-identity"
        value={identity}
        checked={isSelected}
        onChange={onSelect}
        disabled={disabled}
        className="sr-only peer"
      />
      <span
        className={cn(
          'w-full flex items-center justify-between p-3 rounded-xl border transition-all text-left peer-focus-visible:ring-1 peer-focus-visible:ring-brand/40',
          isSelected
            ? 'border-brand bg-brand/10 ring-1 ring-brand/30'
            : 'border-divider bg-bg-tertiary/30 hover:border-brand/30 hover:bg-bg-tertiary/50',
        )}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={cn(
              'h-4 w-4 rounded-full border shrink-0 flex items-center justify-center',
              isSelected ? 'border-brand' : 'border-divider',
            )}
          >
            <span
              className={cn(
                'h-2 w-2 rounded-full',
                isSelected ? 'bg-brand' : 'bg-transparent',
              )}
            />
          </div>
          <div>
            <p className="text-sm font-medium text-text-default">{identity}</p>
            <p className="text-[10px] text-text-tertiary">
              {formatStakedKAI(stakedTokens)} KAI staked
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isActive && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-success/20 text-success">
              Active
            </span>
          )}
        </div>
      </span>
    </label>
  );
}

// Account row component
function AccountRow({
  icon,
  label,
  value,
  connected,
  onConnect,
  onRemove,
  required,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string;
  connected: boolean;
  onConnect?: () => void;
  onRemove?: () => void;
  required?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-4 gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <div
          className={cn(
            'w-9 h-9 rounded-xl flex items-center justify-center shrink-0',
            connected
              ? 'bg-success/10 text-success'
              : 'bg-bg-tertiary text-text-tertiary',
          )}
        >
          {icon}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-text-default">
              {label}
            </span>
            {required && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-bg-secondary text-text-secondary font-medium border border-divider">
                Required
              </span>
            )}
          </div>
          {value && (
            <span className="text-xs text-text-tertiary truncate max-w-[220px] block">
              {value}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span
          className={cn(
            'text-[10px] px-2 py-1 rounded-full border',
            connected
              ? 'bg-success/10 text-success border-success/25'
              : 'bg-bg-tertiary text-text-tertiary border-divider',
          )}
        >
          {connected ? 'Connected' : 'Not connected'}
        </span>
        {connected ? (
          onRemove && (
            <Button
              variant="outline"
              size="xs"
              onClick={onRemove}
              className="border-divider text-text-tertiary hover:text-text-default hover:bg-bg-tertiary"
            >
              Remove
            </Button>
          )
        ) : onConnect ? (
          <Button
            variant="outline"
            size="xs"
            onClick={onConnect}
            className="border-divider hover:bg-bg-tertiary"
          >
            Connect
          </Button>
        ) : null}
      </div>
    </div>
  );
}

type RewardsCardIdentity = {
  identityRaw: string;
  stakedTokens: bigint;
};

export default function ProfilePage() {
  const {
    user,
    authenticated,
    ready,
    linkEmail,
    unlinkEmail,
    linkGoogle,
    unlinkGoogle,
    linkDiscord,
    unlinkDiscord,
    linkTwitter,
    unlinkTwitter,
  } = usePrivy();

  // Use EVM wallet address (not the Privy embedded Solana wallet)
  const { address, isConnected } = useEvmWallet();

  // Identity selection state
  const [selectedIdentity, setSelectedIdentity] = useState<string | null>(null);

  // Fetch shinkai identity data using hooks
  const { data: identityData, isLoading: isLoadingIdentity } =
    useShinkaiIdentityQuery();
  const savedRewardsIdentity = identityData?.selectedShinkaiIdentity ?? null;

  const { mutateAsync: updateSelectedIdentity, isPending: isUpdatingIdentity } =
    useUpdateSelectedIdentity();

  // Sync selected identity with saved identity when it loads
  useEffect(() => {
    if (savedRewardsIdentity !== null && selectedIdentity === null) {
      setSelectedIdentity(savedRewardsIdentity);
    }
  }, [savedRewardsIdentity, selectedIdentity]);

  // Get user's identities
  const { data: identitiesData, isLoading: isLoadingIdentities } =
    useGetIdentities({
      owner: address?.toLowerCase(),
    });

  // Flatten paginated results
  const ownedIdentities = useMemo(
    () =>
      identitiesData?.pages
        .flat()
        .filter((identity): identity is NonNullable<typeof identity> =>
          Boolean(identity),
        ) ?? [],
    [identitiesData],
  );

  const quickAccessIdentities: RewardsCardIdentity[] = useMemo(
    () =>
      ownedIdentities.map((identity) => ({
        identityRaw: identity.identityRaw,
        stakedTokens: BigInt(identity.stakedTokens),
      })),
    [ownedIdentities],
  );

  const { data: savedIdentityData, isLoading: isLoadingSavedIdentity } =
    useGetIdentityData(savedRewardsIdentity ?? '');

  const savedIdentityEntry = savedIdentityData
    ? {
        identityRaw: savedIdentityData.identityRaw,
        stakedTokens: savedIdentityData.stakedTokens,
      }
    : null;

  const displayedIdentities: RewardsCardIdentity[] = useMemo(() => {
    const mapped = [...quickAccessIdentities];

    if (!savedIdentityEntry) {
      return mapped;
    }

    const exists = mapped.some(
      (identity) => identity.identityRaw === savedIdentityEntry.identityRaw,
    );

    if (exists) {
      return mapped;
    }

    return [
      {
        identityRaw: savedIdentityEntry.identityRaw,
        stakedTokens: BigInt(savedIdentityEntry.stakedTokens),
      },
      ...mapped,
    ];
  }, [quickAccessIdentities, savedIdentityEntry]);

  // Save selected identity using hook
  const handleSaveRewardsIdentity = async () => {
    if (!selectedIdentity) return;

    try {
      await updateSelectedIdentity(selectedIdentity);
    } catch (error) {
      console.error('Failed to save rewards identity:', error);
      alert(error instanceof Error ? error.message : 'Failed to save identity');
    }
  };

  const hasChanges = selectedIdentity !== savedRewardsIdentity;

  // Discord verification
  const { data: discordVerificationData } = useDiscordVerifiedStatus();
  const hasDiscordVerified = Boolean(discordVerificationData?.verified);

  // Check requirements for accounts display
  const hasWallet = isConnected && authenticated;
  const hasEmail = Boolean(user?.email?.address);
  const hasDiscord = Boolean(user?.discord);
  const hasGoogle = Boolean(user?.google);
  const hasTwitter = Boolean(user?.twitter);
  const hasRewardIdentity = Boolean(savedRewardsIdentity);
  const isRewardsIdentityLoading =
    isLoadingIdentity ||
    (Boolean(savedRewardsIdentity) && isLoadingSavedIdentity);
  const rewardsSetupRequirements = [
    { label: 'Wallet connected', completed: hasWallet },
    { label: 'Email connected', completed: hasEmail },
    { label: 'Discord connected', completed: hasDiscord },
    { label: 'Discord verified', completed: hasDiscordVerified },
  ];
  const completedRewardsSetupCount = rewardsSetupRequirements.filter(
    (requirement) => requirement.completed,
  ).length;
  const rewardsSetupPercentage = Math.round(
    (completedRewardsSetupCount / rewardsSetupRequirements.length) * 100,
  );
  const requiredAccountsConnectedCount = [
    hasWallet,
    hasEmail,
    hasDiscord,
  ].filter(Boolean).length;
  const optionalAccountsConnectedCount = [hasGoogle, hasTwitter].filter(
    Boolean,
  ).length;

  return (
    <main className="grow pb-20 px-6 font-inter">
      <div className="max-w-3xl mx-auto space-y-10">
        <IsConnectedWrapper>
          {/* Header */}
          <section className="space-y-2">
            <h1 className="font-clash text-4xl font-medium text-text-default tracking-tight">
              Profile Settings
            </h1>
            <p className="text-base text-text-secondary">
              Manage your rewards setup, identities, and connected accounts
            </p>
          </section>

          {/* Rewards Setup */}
          <section className="space-y-4">
            <SectionHeader
              title="Rewards Setup"
              subtitle="Track your setup progress and complete all required items to unlock rewards."
              right={
                <span className="text-[11px] px-2 py-1 rounded-full border border-divider text-text-tertiary">
                  {completedRewardsSetupCount}/{rewardsSetupRequirements.length}{' '}
                  complete
                </span>
              }
            />
            <RewardsCard variant="full" />
          </section>

          {/* My Identities Quick Access */}
          {ready && (
            <section className="space-y-4">
              <SectionHeader
                title="My Shinkai Identities"
                subtitle="Quick reference list of all your identities."
                right={
                  quickAccessIdentities.length > 0 ? (
                    <span className="text-[11px] px-2 py-1 rounded-full border border-divider text-text-tertiary">
                      {quickAccessIdentities.length} total
                    </span>
                  ) : undefined
                }
              />
              <div className="rounded-2xl border border-divider bg-bg-secondary/20 overflow-hidden">
                {isLoadingIdentities ? (
                  <div className="p-6 flex items-center justify-center gap-2 text-sm text-text-tertiary">
                    <Loader2 className="w-4 h-4 animate-spin text-brand" />
                    Loading identities...
                  </div>
                ) : quickAccessIdentities.length > 0 ? (
                  <div className="p-4 space-y-2">
                    {quickAccessIdentities.map((identity) => (
                      <IdentityQuickAccessCard
                        key={identity.identityRaw}
                        identity={identity.identityRaw}
                        stakedTokens={identity.stakedTokens}
                        isActive={savedRewardsIdentity === identity.identityRaw}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center space-y-3">
                    <p className="text-sm text-text-secondary">
                      No identities found yet
                    </p>
                    <p className="text-xs text-text-tertiary">
                      Register your first identity to see it here
                    </p>
                    <div>
                      <Button asChild variant="outline" size="xs">
                        <Link to="/register">Register Identity</Link>
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Reward Identity Selection */}
          {ready && (
            <section className="space-y-4">
              <SectionHeader
                title="Reward Identity Selection"
                subtitle="Choose which identity earns rewards using the selection below."
              />
              <div className="rounded-2xl border border-divider bg-bg-secondary/20 overflow-hidden">
                {isRewardsIdentityLoading ? (
                  <div className="p-6 flex items-center justify-center gap-2 text-sm text-text-tertiary">
                    <Loader2 className="w-4 h-4 animate-spin text-brand" />
                    Loading reward identity options...
                  </div>
                ) : displayedIdentities.length > 0 ? (
                  <div className="p-4 space-y-3">
                    {displayedIdentities.map((identity) => (
                      <IdentitySelectCard
                        key={identity.identityRaw}
                        identity={identity.identityRaw}
                        stakedTokens={identity.stakedTokens}
                        isSelected={selectedIdentity === identity.identityRaw}
                        isActive={savedRewardsIdentity === identity.identityRaw}
                        onSelect={() =>
                          setSelectedIdentity(identity.identityRaw)
                        }
                        disabled={isUpdatingIdentity}
                      />
                    ))}
                    {hasChanges && (
                      <div className="pt-2 border-t border-divider flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="xs"
                          onClick={() =>
                            setSelectedIdentity(savedRewardsIdentity)
                          }
                          disabled={isUpdatingIdentity}
                          className="border-divider"
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="default"
                          size="xs"
                          onClick={handleSaveRewardsIdentity}
                          disabled={isUpdatingIdentity || !selectedIdentity}
                          className="gap-2"
                        >
                          {isUpdatingIdentity ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4" />
                              Save Identity
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-8 text-center space-y-3">
                    <p className="text-sm text-text-secondary">
                      No Shinkai identities found
                    </p>
                    <p className="text-xs text-text-tertiary">
                      Create an identity to start earning rewards
                    </p>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Connected Accounts */}
          <section className="space-y-4">
            <SectionHeader
              title="Connected Accounts"
              subtitle="Required and optional account links with clear connection status."
            />
            <div className="rounded-2xl border border-divider bg-bg-secondary/20 p-4 space-y-5">
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-sm font-medium text-text-default">
                    Required Accounts
                  </h3>
                  <span className="text-[11px] px-2 py-1 rounded-full border border-divider text-text-tertiary">
                    {requiredAccountsConnectedCount}/3 connected
                  </span>
                </div>
                <div className="rounded-xl border border-divider bg-bg-tertiary/20 px-4 divide-y divide-divider">
                  <AccountRow
                    icon={<Wallet className="w-4 h-4" />}
                    label="Wallet"
                    value={address}
                    connected={hasWallet}
                    required
                  />
                  <AccountRow
                    icon={<Mail className="w-4 h-4" />}
                    label="Email"
                    value={user?.email?.address}
                    connected={hasEmail}
                    onConnect={() => linkEmail()}
                    onRemove={() => {
                      if (user?.email?.address) {
                        unlinkEmail(user.email.address);
                      }
                    }}
                    required
                  />
                  <AccountRow
                    icon={<DiscordIcon className="w-4 h-4" />}
                    label="Discord"
                    value={
                      user?.discord?.username
                        ? `@${user.discord.username}`
                        : undefined
                    }
                    connected={hasDiscord}
                    onConnect={() => linkDiscord()}
                    onRemove={() => {
                      if (user?.discord?.subject) {
                        unlinkDiscord(user.discord.subject);
                      }
                    }}
                    required
                  />
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-sm font-medium text-text-default">
                    Optional Accounts
                  </h3>
                  <span className="text-[11px] px-2 py-1 rounded-full border border-divider text-text-tertiary">
                    {optionalAccountsConnectedCount}/2 connected
                  </span>
                </div>
                <div className="rounded-xl border border-divider bg-bg-tertiary/20 px-4 divide-y divide-divider">
                  <AccountRow
                    icon={<GoogleIcon className="w-4 h-4" />}
                    label="Google"
                    value={user?.google?.email}
                    connected={hasGoogle}
                    onConnect={() => linkGoogle()}
                    onRemove={() => {
                      if (user?.google?.subject) {
                        unlinkGoogle(user.google.subject);
                      }
                    }}
                  />
                  <AccountRow
                    icon={<TwitterIcon className="w-4 h-4" />}
                    label="X (Twitter)"
                    value={
                      user?.twitter?.username
                        ? `@${user.twitter.username}`
                        : undefined
                    }
                    connected={hasTwitter}
                    onConnect={() => linkTwitter()}
                    onRemove={() => {
                      if (user?.twitter?.subject) {
                        unlinkTwitter(user.twitter.subject);
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </section>
        </IsConnectedWrapper>
      </div>
    </main>
  );
}
